import { Component } from '@theme/component';

/**
 * A custom element that manages consultant filtering
 *
 * @typedef {object} Refs
 * @property {HTMLSelectElement} filter-options – The filter options select element.
 *
 * @extends Component<Refs>
 */
export class RepFilterBar extends Component {
  requiredRefs = ['filter-options'];

  connectedCallback() {
    super.connectedCallback();
    /** @type {string[]} */
    this.eligibleProvinces = [];
    this.onChange = this.#handleChange.bind(this);
    this.repList = document.getElementById('rep-provinces');
    this.reps = this.repList?.querySelectorAll('[data-custom-attributes]');
    if(!this.reps) return;
    const filterOptions = /** @type {HTMLSelectElement} */ (this.refs['filter-options']);
    filterOptions?.addEventListener('change', this.onChange);
    this.determineEligibleProvinces();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const filterOptions = /** @type {HTMLSelectElement} */ (this.refs['filter-options']);
    filterOptions?.removeEventListener('change', this.onChange);
  }

  /**
   * Handles change events on the filter options.
   *
   * @param {Event} event - The change event.
   */
  #handleChange(event) {
    if(!event) return;
    const target = /** @type {HTMLSelectElement} */ (event.target);
    if(!target) return;
    const id = target.value;
    if(!id) return;
    this.#filterLocations(id);
  }

   /**
   * Filters page elements by a passed ID
   *
   * @param {string} id - The ID to filter by.
   */
  #filterLocations(id) {
    if(!id) return;
    if(!this.reps) return;
    if(id === 'all') {
        this.showAllReps();
        this.filterGroups();
        return;
    }
    this.reps.forEach((rep) => {
        const attributes = rep.getAttribute('data-custom-attributes');
        if(!attributes || attributes === "rep-group") return;
        const attrArray = attributes.split(',').map(attr => {
            return attr.split("-")[1];
        });
        if(attrArray.includes(id)) {
            rep.classList.remove('hidden');
        } else {
            rep.classList.add('hidden');
        }
    });
    this.filterGroups();
  }

  showAllReps() {
    if(!this.reps) return;
    this.reps.forEach((rep) => {
        rep.classList.remove('hidden');
        const parent = rep.closest(".group-block-content");
        if(!parent) return;
        parent.classList.remove('hidden');
    });
  }

  /* Using builder blocks means empty groups will retain whitespace in the grid layout, empty groups need to be hidden */
  filterGroups() {
    const groups = this.repList?.querySelectorAll(`[data-custom-attributes="rep-group"]`);
    if(!groups) return;
    groups.forEach((group) => {
        const hasVisibleReps = group.querySelectorAll('[data-custom-attributes]:not(.hidden)');
        if(hasVisibleReps.length > 0) {
            group.classList.remove('hidden');
        } else {
            group.classList.add('hidden');
        }
    });
  }

  determineEligibleProvinces() {
    if(!this.reps) return;
    this.reps.forEach((rep) => {
        const attributes = rep.getAttribute('data-custom-attributes');
        if(!attributes || attributes === "rep-group") return;
        const attrArray = attributes.split(',').map(attr => {
            return attr.split("-")[1];
        });
        attrArray.forEach((attr) => {
            if(!this.eligibleProvinces?.includes(attr)) {
                this.eligibleProvinces?.push(attr);
            }
        });
    });

    for (var i = 0; i < this.refs['filter-options'].options.length; i++) {
        const option = this.refs['filter-options'].options[i];
        if(!option) continue;
        if(!this.eligibleProvinces?.includes(option.value) && option.value !== 'all') {
            option.disabled = true;
        }
    }
  }

}

if (!customElements.get('rep-filter-bar')) customElements.define('rep-filter-bar', RepFilterBar);
